<template>
  <label class="base-checkbox">
    <input
      :id="id"
      :aria-describedby="ariaDescribedby || undefined"
      :aria-invalid="ariaInvalid || undefined"
      :checked="modelValue"
      class="input"
      :disabled="disabled"
      :required="required"
      type="checkbox"
      @change="handleChange"
    />
    <span
      v-if="label"
      class="label"
    >
      {{ label }}
    </span>
  </label>
</template>

<script setup lang="ts">
/**
 * Checkbox input component with v-model support
 *
 * Provides a styled checkbox with optional label text.
 * Label acts as click target for improved accessibility.
 *
 * @example Basic checkbox
 * ```vue
 * <BaseCheckbox v-model="agree" label="I agree to the terms" />
 * ```
 *
 * @example Without label (use aria-label)
 * ```vue
 * <BaseCheckbox v-model="enabled" aria-label="Enable notifications" />
 * ```
 *
 * @example Required checkbox
 * ```vue
 * <BaseCheckbox
 *   v-model="consent"
 *   label="I consent to data processing"
 *   required
 * />
 * ```
 */
export interface BaseCheckboxProps {
  /** Current checked state (use with v-model) */
  modelValue: boolean;

  /** Optional label text displayed next to checkbox */
  label?: string;

  /**
   * Disables the checkbox and prevents interaction
   * @default false
   */
  disabled?: boolean;

  /**
   * Marks checkbox as required for form validation
   * @default false
   */
  required?: boolean;

  /** Unique element ID for label association */
  id?: string;

  /** ID of element describing this checkbox (typically error message ID) */
  ariaDescribedby?: string;

  /** Indicates the checkbox has an error for accessibility */
  ariaInvalid?: boolean;
}

withDefaults(defineProps<BaseCheckboxProps>(), {
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.checked);
};
</script>

<style>
.base-checkbox {
  display: inline-flex;
  align-items: center;
  gap: var(--gap-xs);
  cursor: pointer;
  user-select: none;

  .input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    cursor: inherit;
  }

  &:has(.input:disabled) {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .label {
    font-size: var(--font-md);
    line-height: 1.5;
  }
}
</style>
